import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { storyService } from '../services/storyService';
import DeleteStoryModal from './DeleteStoryModal';
import EditStoryModal from './EditStoryModal';

const StoryList = () => {
  const [stories, setStories] = useState([]);
  const [publicStories, setPublicStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [filteredPublicStories, setFilteredPublicStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [activeTab, setActiveTab] = useState('my-stories');
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadStories();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'community') {
      loadPublicStories();
    }
  }, [activeTab]);

  // Filter stories based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredStories(stories);
    } else {
      const filtered = stories.filter(story =>
        story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (story.description && story.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (story.author_name && story.author_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredStories(filtered);
    }
  }, [searchQuery, stories]);

  // Filter public stories based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPublicStories(publicStories);
    } else {
      const filtered = publicStories.filter(story =>
        story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (story.description && story.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (story.author_username && story.author_username.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredPublicStories(filtered);
    }
  }, [searchQuery, publicStories]);

  const loadStories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await storyService.getUserStories();
      setStories(data);
      setFilteredStories(data);
    } catch (err) {
      console.error('Error loading stories:', err);
      setError('Failed to load stories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPublicStories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await storyService.getPublicStories();
      setPublicStories(data);
      setFilteredPublicStories(data);
    } catch (err) {
      console.error('Error loading public stories:', err);
      setError('Failed to load community stories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStory = () => {
    navigate('/create');
  };

  const handlePlayStory = (storyId) => {
    navigate(`/story/${storyId}/play`);
  };

  const handleEditStory = (story) => {
    setSelectedStory(story);
    setEditModalOpen(true);
  };

  const handleDeleteStory = (story) => {
    setSelectedStory(story);
    setDeleteModalOpen(true);
  };

  const handleStoryGraph = (storyId) => {
    navigate(`/story/${storyId}/edit`);
  };

  const confirmDeleteStory = async () => {
    if (!selectedStory) return;

    try {
      await storyService.deleteStory(selectedStory.id);
      setStories(stories.filter(s => s.id !== selectedStory.id));
      setDeleteModalOpen(false);
      setSelectedStory(null);
    } catch (err) {
      console.error('Error deleting story:', err);
      setError('Failed to delete story. Please try again.');
    }
  };

  const handleSaveStory = async (updatedStory) => {
    try {
      const saved = await storyService.updateStory(updatedStory.id, updatedStory);
      setStories(stories.map(s => s.id === saved.id ? saved : s));
      setEditModalOpen(false);
      setSelectedStory(null);
    } catch (err) {
      console.error('Error updating story:', err);
      setError('Failed to update story. Please try again.');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const getVisibilityBadge = (story) => {
    if (story.visibility === 'private') {
      return (
        <div className="flex items-center gap-1 bg-white bg-opacity-90 px-2 py-1 rounded-lg shadow-soft">
          <SafeIcon icon={FiIcons.FiLock} className="text-xs text-gray-600" />
          <span className="text-xs text-gray-600">Private</span>
        </div>
      );
    } else if (story.visibility === 'shared') {
      return (
        <div className="flex items-center gap-1 bg-white bg-opacity-90 px-2 py-1 rounded-lg shadow-soft">
          <SafeIcon icon={FiIcons.FiLink} className="text-xs text-purple-600" />
          <span className="text-xs text-purple-600">Shared</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 bg-white bg-opacity-90 px-2 py-1 rounded-lg shadow-soft">
          <SafeIcon icon={FiIcons.FiGlobe} className="text-xs text-green-600" />
          <span className="text-xs text-green-600">Public</span>
        </div>
      );
    }
  };

  const renderStoryCard = (story, isPublic = false) => (
    <motion.div
      key={story.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-3xl shadow-dreamy p-6 hover:shadow-glow transition-all duration-300 relative"
    >
      {/* Visibility Badge */}
      <div className="absolute top-2 left-2">
        {getVisibilityBadge(story)}
      </div>

      {/* Story Image */}
      <div className="mb-4 rounded-xl overflow-hidden h-48 bg-gradient-to-br from-purple-100 to-pink-100">
        {story.story_image_url ? (
          <img
            src={story.story_image_url}
            alt={story.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <SafeIcon icon={FiIcons.FiBook} className="text-4xl text-purple-300" />
          </div>
        )}
      </div>

      {/* Story Content */}
      <div className="mb-4">
        <h3 className="text-xl font-display font-bold text-purple-700 mb-2">
          {story.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3">
          {story.description || 'No description available'}
        </p>
      </div>

      {/* Story Meta */}
      <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <SafeIcon icon={FiIcons.FiCalendar} />
          <span>{new Date(story.created_at).toLocaleDateString()}</span>
        </div>
        {isPublic && (
          <div className="flex items-center gap-1">
            <SafeIcon icon={FiIcons.FiUser} />
            <span>by {story.author_username || 'Anonymous'}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-xl font-medium flex items-center justify-center gap-2"
          onClick={() => handlePlayStory(story.id)}
        >
          <SafeIcon icon={FiIcons.FiPlay} className="text-lg" />
          Play
        </motion.button>

        {!isPublic && (
          <>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-purple-100 hover:bg-purple-200 text-purple-700 py-2 px-4 rounded-xl font-medium flex items-center gap-2"
              onClick={() => handleStoryGraph(story.id)}
            >
              <SafeIcon icon={FiIcons.FiEdit} className="text-lg" />
              Edit
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-xl font-medium"
              onClick={() => handleEditStory(story)}
            >
              <SafeIcon icon={FiIcons.FiSettings} className="text-lg" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-red-100 hover:bg-red-200 text-red-700 py-2 px-3 rounded-xl font-medium"
              onClick={() => handleDeleteStory(story)}
            >
              <SafeIcon icon={FiIcons.FiTrash2} className="text-lg" />
            </motion.button>
          </>
        )}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading stories...</p>
      </div>
    );
  }

  const currentStories = activeTab === 'my-stories' ? filteredStories : filteredPublicStories;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="story-list"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-display font-bold text-purple-700">
          Interactive Stories
        </h1>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
          onClick={handleCreateStory}
        >
          <SafeIcon icon={FiIcons.FiPlus} className="text-lg" />
          Create Story
        </motion.button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'my-stories'
              ? 'bg-primary-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => handleTabChange('my-stories')}
        >
          My Stories
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'community'
              ? 'bg-primary-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => handleTabChange('community')}
        >
          Community Stories
        </motion.button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <SafeIcon
            icon={FiIcons.FiSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder={`Search ${activeTab === 'my-stories' ? 'your stories' : 'community stories'}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
          />
          {searchQuery && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchQuery('')}
            >
              <SafeIcon icon={FiIcons.FiX} />
            </motion.button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Search Results Info */}
      {searchQuery && (
        <div className="mb-4 text-sm text-gray-600">
          {currentStories.length} result{currentStories.length !== 1 ? 's' : ''} found for "{searchQuery}"
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentStories.length === 0 ? (
          <div className="col-span-full text-center py-12">
            {searchQuery ? (
              <>
                <div className="mb-4 text-purple-300">
                  <SafeIcon icon={FiIcons.FiSearch} className="text-6xl mx-auto" />
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-2">
                  No Results Found
                </h3>
                <p className="text-gray-600 mb-6">
                  No stories match your search for "{searchQuery}". Try different keywords.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </motion.button>
              </>
            ) : activeTab === 'my-stories' ? (
              <>
                <div className="mb-4 text-purple-300">
                  <SafeIcon icon={FiIcons.FiBook} className="text-6xl mx-auto" />
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-2">
                  No Stories Yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Create your first interactive story to get started!
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto"
                  onClick={handleCreateStory}
                >
                  <SafeIcon icon={FiIcons.FiPlus} className="text-lg" />
                  Create Your First Story
                </motion.button>
              </>
            ) : (
              <>
                <div className="mb-4 text-purple-300">
                  <SafeIcon icon={FiIcons.FiGlobe} className="text-6xl mx-auto" />
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-2">
                  No Public Stories Yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Be the first to share a story with the community!
                </p>
              </>
            )}
          </div>
        ) : (
          currentStories.map(story => renderStoryCard(story, activeTab === 'community'))
        )}
      </div>

      {/* Delete Modal */}
      <DeleteStoryModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDeleteStory}
        storyTitle={selectedStory?.title || ''}
      />

      {/* Edit Modal */}
      <EditStoryModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveStory}
        story={selectedStory}
      />
    </motion.div>
  );
};

export default StoryList;